import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import { useAuth } from '../../../auth/AuthContext';
import EditButton from '../../ui/EditButton';
import { STRIPE_ACCOUNT_INFO_URL, CREATE_ACCOUNT_LINK_URL, UPDATE_STRIPE_PAYOUT_SCHEDULE_URL } from '../../../utils/functionURLs';
import LoadingModal from '../../ui/LoadingModal';

const PAYOUT_INTERVAL_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'manual', label: 'Manual' },
];

const WEEKDAY_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1;
  return { value: String(day), label: `Day ${day}` };
});

function buildPayoutForm(schedule = {}) {
  return {
    interval: String(schedule?.interval || 'daily').trim().toLowerCase() || 'daily',
    weeklyAnchor: String(schedule?.weekly_anchor || 'monday').trim().toLowerCase() || 'monday',
    monthlyAnchor: String(schedule?.monthly_anchor || 1),
  };
}

function buildComparableSchedule(form = {}) {
  const interval = String(form.interval || 'daily').trim().toLowerCase() || 'daily';
  if (interval === 'weekly') {
    return {
      interval,
      weeklyAnchor: String(form.weeklyAnchor || 'monday').trim().toLowerCase() || 'monday',
    };
  }
  if (interval === 'monthly') {
    return {
      interval,
      monthlyAnchor: String(form.monthlyAnchor || '1'),
    };
  }
  return { interval };
}

function formatPayoutSchedule(schedule = {}) {
  const interval = String(schedule?.interval || '').trim().toLowerCase();
  if (interval === 'daily') return 'Daily';
  if (interval === 'manual') return 'Manual';
  if (interval === 'weekly') {
    const label = WEEKDAY_OPTIONS.find(option => option.value === schedule?.weekly_anchor)?.label || 'selected day';
    return `Weekly on ${label}`;
  }
  if (interval === 'monthly') {
    const day = Number(schedule?.monthly_anchor);
    return Number.isInteger(day) && day >= 1 && day <= 31 ? `Monthly on day ${day}` : 'Monthly';
  }
  return 'Not available';
}

export default function StripeCard({
  teamId,
  refreshKey,
  setStripeStatus
}) {
  const {
    user
  } = useAuth();
  const [accountId, setAccountId] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payoutForm, setPayoutForm] = useState(buildPayoutForm());
  const [payoutError, setPayoutError] = useState(null);
  const [payoutNotice, setPayoutNotice] = useState(null);
  const [savingPayoutSchedule, setSavingPayoutSchedule] = useState(false);
  useEffect(() => {
    if (!teamId || !user) return;
    const fetchStripeStatus = async () => {
      try {
        setError(null);
        const stripeRef = doc(db, 'teams', teamId, 'stripe', 'setup');
        const snap = await getDoc(stripeRef);
        if (!snap.exists()) {
          setAccountId(null);
          setAccountInfo(null);
          setError('Stripe account not set up.');
          return;
        }
        const data = snap.data();
        if (!data.accountId) {
          setAccountId(null);
          setAccountInfo(null);
          setError('Stripe account not connected.');
          return;
        }
        setAccountId(data.accountId);
        const token = await user.getIdToken();
        const res = await fetch(STRIPE_ACCOUNT_INFO_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            teamId,
            accountId: data.accountId
          })
        });
        if (!res.ok) throw new Error((await res.json())?.error || 'Unable to retrieve account details');
        const accountData = await res.json();
        setAccountInfo(accountData);
        setPayoutForm(buildPayoutForm(accountData?.payout_schedule));
        setPayoutError(null);
        setPayoutNotice(null);
        if (accountData && typeof setStripeStatus === 'function') {
          setStripeStatus(accountData);
        }
      } catch (err) {
        setAccountInfo(null);
        setError(err.message || String(err));
      }
    };
    fetchStripeStatus();
  }, [teamId, user, setStripeStatus, refreshKey]);

  const handlePayoutFormChange = (field, value) => {
    setPayoutError(null);
    setPayoutNotice(null);
    setPayoutForm(current => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSavePayoutSchedule = async () => {
    if (!accountInfo || !user) return;

    setSavingPayoutSchedule(true);
    setPayoutError(null);
    setPayoutNotice(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(UPDATE_STRIPE_PAYOUT_SCHEDULE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId,
          accountId,
          interval: payoutForm.interval,
          weeklyAnchor: payoutForm.weeklyAnchor,
          monthlyAnchor: Number(payoutForm.monthlyAnchor),
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Unable to update payout schedule.');
      }

      const nextAccountInfo = {
        ...accountInfo,
        payout_schedule: data?.payout_schedule || {},
      };

      setAccountInfo(nextAccountInfo);
      setPayoutForm(buildPayoutForm(data?.payout_schedule));
      setPayoutNotice('Payout schedule updated.');
      if (typeof setStripeStatus === 'function') {
        setStripeStatus(nextAccountInfo);
      }
    } catch (err) {
      setPayoutError(err?.message || 'Unable to update payout schedule.');
    }

    setSavingPayoutSchedule(false);
  };

  const handleFinishSetup = async () => {
    setLoading(true);
    try {
      if (!accountId) {
        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        const teamCountry = String(teamSnap.data()?.address?.country || '').trim();
        if (!teamCountry) {
          throw new Error('Set your team address country first (Address card), then connect Stripe.');
        }
      }
      const token = await user.getIdToken();
      const res = await fetch(CREATE_ACCOUNT_LINK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Unable to open Stripe.');
      }
      if (!data.url) {
        throw new Error('Stripe link not available.');
      }
      window.location.href = data.url;
    } catch (err) {
      alert(err?.message || 'Unable to open Stripe. Try again later.');
    }
    setLoading(false);
  };

  const currentSchedule = buildComparableSchedule(buildPayoutForm(accountInfo?.payout_schedule));
  const draftSchedule = buildComparableSchedule(payoutForm);
  const payoutScheduleDirty = JSON.stringify(currentSchedule) !== JSON.stringify(draftSchedule);
  const canEditPayoutSchedule = Boolean(accountInfo?.type === 'express' && accountInfo?.details_submitted);

  return <>
    {loading && <LoadingModal message="Fetching Stripe…" />}
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Stripe Account</span>
        <EditButton section="stripe" onEdit={handleFinishSetup} />
      </div>

      <div className="card-body">
        {(!accountInfo?.details_submitted || !accountInfo?.charges_enabled || !accountInfo?.payouts_enabled || !accountInfo?.transfers_enabled) && (
          <button type="button" className="btn btn--primary btn--sm" onClick={handleFinishSetup} disabled={loading}>
            {accountId ? 'Continue Stripe setup' : 'Connect Stripe'}
          </button>
        )}
        {error && <p className="text-error">{error}</p>}

        {!accountId || !accountInfo ? <p className="text-muted">Not connected to Stripe.</p> : <>
            <p><strong>Email:</strong> {accountInfo.email || 'Not available'}</p>
            <p><strong>Details Submitted:</strong> {accountInfo.details_submitted ? 'Yes' : 'No'}</p>
            <p><strong>Transfers Enabled:</strong> {accountInfo.transfers_enabled ? 'Yes' : 'No'}</p>
            <p><strong>Payouts Enabled:</strong> {accountInfo.payouts_enabled ? 'Yes' : 'No'}</p>
            <p><strong>Payout Schedule:</strong> {formatPayoutSchedule(accountInfo.payout_schedule)}</p>

            {canEditPayoutSchedule ? <div style={{
              marginTop: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid rgba(15, 23, 42, 0.08)'
            }}>
                <label className="form-label">Payout timing</label>
                <select className="form-input" value={payoutForm.interval} onChange={e => handlePayoutFormChange('interval', e.target.value)}>
                  {PAYOUT_INTERVAL_OPTIONS.map(option => <option key={option.value} value={option.value}>
                      {option.label}
                    </option>)}
                </select>

                {payoutForm.interval === 'weekly' ? <div style={{
                  marginTop: '0.75rem'
                }}>
                    <label className="form-label">Weekly payout day</label>
                    <select className="form-input" value={payoutForm.weeklyAnchor} onChange={e => handlePayoutFormChange('weeklyAnchor', e.target.value)}>
                      {WEEKDAY_OPTIONS.map(option => <option key={option.value} value={option.value}>
                          {option.label}
                        </option>)}
                    </select>
                  </div> : null}

                {payoutForm.interval === 'monthly' ? <div style={{
                  marginTop: '0.75rem'
                }}>
                    <label className="form-label">Monthly payout day</label>
                    <select className="form-input" value={payoutForm.monthlyAnchor} onChange={e => handlePayoutFormChange('monthlyAnchor', e.target.value)}>
                      {MONTH_DAY_OPTIONS.map(option => <option key={option.value} value={option.value}>
                          {option.label}
                        </option>)}
                    </select>
                  </div> : null}

                <p className="text-muted" style={{
                  marginTop: '0.75rem'
                }}>
                  This controls when Stripe automatically pays out available funds for this connected account.
                </p>

                {payoutError ? <p className="text-error" style={{
                  marginTop: '0.5rem'
                }}>
                    {payoutError}
                  </p> : null}

                {payoutNotice ? <p className="text-muted" style={{
                  marginTop: '0.5rem'
                }}>
                    {payoutNotice}
                  </p> : null}

                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  marginTop: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  <button type="button" className="btn btn--primary btn--sm" onClick={handleSavePayoutSchedule} disabled={!payoutScheduleDirty || savingPayoutSchedule}>
                    {savingPayoutSchedule ? 'Saving…' : 'Save payout schedule'}
                  </button>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => {
                    setPayoutForm(buildPayoutForm(accountInfo?.payout_schedule));
                    setPayoutError(null);
                    setPayoutNotice(null);
                  }} disabled={!payoutScheduleDirty || savingPayoutSchedule}>
                    Reset
                  </button>
                </div>
              </div> : <p className="text-muted" style={{
              marginTop: '0.75rem'
            }}>
                {accountInfo?.type !== 'express'
                  ? 'Payout schedule editing on this card is only available for Express accounts.'
                  : 'Complete Stripe onboarding to manage payout timing here.'}
              </p>}
          </>}
      </div>
    </div>
    </>;
}
